// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from '@emotion/styled';
import uniqueId from 'lodash/uniqueId';
import { Info } from 'lucide-react';

import { formatDate } from 'Utils/formatters';
import formatWeeksAndDays from 'Utils/formatWeeksAndDays';

const StyledTooltip = styled.div`
  .reservedStock {
    font-size: 10px;
    border: 1px solid #707070;
    font-weight: 600;
    padding: 1px 3px;
    margin: 0 5px;
  }

  .stock {
    &.inStockAndReserved {
      font-weight: 600;

      .stockAmount {
        font-weight: normal;
        color: #68A765;
        margin: 0 5px;

        &.noStock {
          color: #D52B1E;
        }
      }

      .stockAmountReserved {
        font-weight: normal;
        color: #3a3a3a;
        margin: 0 5px;
      }

      .stockTooltip {
        display: flex;
        flex-direction: column;
        box-shadow: 0 0 8px #00000029;
        max-width: 230px;
        padding: 0;
        font-size: 14px;
        opacity: 1;
        text-align: left;
        color: #3a3a3a;
        cursor: pointer;
        z-index: 9999;

        p {
            border-top: 1px solid #F3F2F2;
            padding: 10px 20px 20px 20px;
            font-weight: 400;
            font-size: 14px;
            color: #3a3a3a;

            span {
              color: #D52B1E !important;
            }
          }

        .stockInfo {
          padding: 20px 20px 10px 20px;
          display: flex;

          > div {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
          }

          span {
            color: #3a3a3a;
            font-weight: normal;
            margin: 0 0 5px;

            &.stockAmount {
              color: #68A765;
            }

            &.supplierAmount {
              color: #3749EE;
            }
          }

          .stockInfoLeft span {
            font-weight: 700;
          }
        }
      }
    }
  }
`;

const StyledSupplierOffer = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-weight: 600;
  color: #3a3a3a;

  &.table {
    flex-direction: column;

    span {
      color: #3a3a3a;
    }
  }

  span {
    font-weight: normal;
    color: #3749EE;
  }

  .react-tooltip {
    font-weight: normal;
    max-width: 230px;
    text-align: left;
  }
`;

export const renderStock = ({ product, t, info }) => {
  const uid = uniqueId();
  if (!product?.hasPrice) {
    return (
      <StyledSupplierOffer className="stock supplierOffer">
        { t('product.supplierStock') }:
        <span className="stockAmount">{ product.stockValue } { t('products.products') }</span>
        <Info size={ 18 } id={ `supplier-stock-tooltip-${ uid }}` } />
        <ReactTooltip
          className="stockTooltip"
          anchorId={ `supplier-stock-tooltip-${ uid }}` }
          variant="light"
          offset={ 10 }
          content={ t('products.supplierDeliveryEstimateMessage', { deliveryEstimate: formatWeeksAndDays(product?.supplierOfferDeliveryTimeDays || '') }) }
        />
      </StyledSupplierOffer>
    );
  }

  if (
    (
      product.stockValue >= product.minQuantity
      && product.reservedStockValue >= product.minQuantity
    ) || (
      product.supplierOffersTotalStock > 0
      && product.reservedStockValue <= 3
    )
  ) {
    return (
      <StyledTooltip>
        <div className="stock inStockAndReserved">
          { (product.stockValue === 0 && product.reservedStockValue > 0) ? (
            <>
              { t('products.stockReserved') }:
              <span className="stockAmountReserved">{ product.reservedStockValue } { t('products.products') }</span>
              <span id={ `stock-tooltip-${ uid }}` } className="reservedStock">+{ product.supplierOffersTotalStock }</span>
            </>
          ) : (
            <>
              { t('products.stock') }:
              <span className={ `stockAmount ${ product.stockValue === 0 ? 'noStock' : '' }` }>{ product.stockValue } { t('products.products') }</span>
              <span id={ `stock-tooltip-${ uid }}` } className="reservedStock">+{ product.reservedStockValue + product.supplierOffersTotalStock }</span>
            </>
          )}
          <ReactTooltip
            className="stockTooltip"
            anchorId={ `stock-tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <>
                <div className="stockInfo">
                  <div className="stockInfoLeft">
                    { (product.stockValue > 0) && <span>{ t('products.inStock') }:</span> }
                    { (product.reservedStockValue > 0) && <span>{ t('products.reserved') }:</span> }
                    { (product.supplierOffersTotalStock > 0) && <span>{ t('products.supplierStock') }:</span> }
                  </div>
                  <div className="stockInfoRight">
                    { (product.stockValue > 0) && <span className="stockAmount">{ product.stockValue } { t('products.products') }</span> }
                    { (product.reservedStockValue > 0) && <span>{ product.reservedStockValue } { t('products.products') }</span> }
                    { (product.supplierOffersTotalStock > 0) && <span className="supplierAmount">{ product.supplierOffersTotalStock } { t('products.products') }</span> }
                  </div>
                </div>
                { info?.accountManagerPhone && <p>{ t('products.rsvpDetailsAt') }: <span>{ info?.accountManagerPhone }</span></p> }
              </>
            ) }
          />
        </div>
      </StyledTooltip>
    );
  }

  if (product.stockValue >= product.minQuantity) {
    return (
      <p className="stock inStock">
        { t('products.stock') }:
        <span>{ product.stockValue } { t('products.products') }</span>
      </p>
    );
  }

  if (product.reservedStockValue >= product.minQuantity) {
    return (
      <p className="stock reserved">
        { t('products.stockReserved') }:
        <span>{ product.reservedStockValue } { t('products.products') }</span>
        <span className="reservedInfo">({ info?.accountManagerPhone ? <>{ t('products.rsvpDetailsAt') } <span className="danger">{ info?.accountManagerPhone }</span></> : t('products.rsvpDetails') })</span>
      </p>
    );
  }

  if (!(product.stockValue > 0 && product.reservedStockValue > 0) && product.inCsp) {
    return (
      <p className="stock inCsp">
        { t('products.stock') }:
        <span>{ t('products.inCsp') }</span>
      </p>
    );
  }

  if (product.supplierStockValue > 0 || product.isOnDemandOnly) {
    if (product.supplierStockDeliveryDate) {
      return (
        <p className="stock onDemand">
          { t('products.stock') }:
          <span>{ t('products.outOfStock') } ({ product.supplierStockValue } { t('products.units') } { t('products.availableStartingFrom') } { formatDate(product.supplierStockDeliveryDate, { timeStyle: undefined }) })
          </span>
        </p>
      );
    }
    if (product.deliveryTime) {
      return (
        <p className="stock onDemand">
          { t('products.stock') }:
          <span>{ t('products.onDemand') } ({ t('products.deliveryTime', { days: product.deliveryTime }) })</span>
        </p>
      );
    }
    return (
      <p className="stock onDemand">
        { t('products.stock') }:
        <span>{ t('products.outOfStock') } ({ t('products.onDemand') })</span>
      </p>
    );
  }

  return (
    <p className="stock outOfStock">
      { t('products.stock') }:
      <span>{ product?.isEol ? t('products.outOfStock') : '0' }</span>
    </p>
  );
};

export const renderGridStock = ({ product, t, info }) => {
  const uid = uniqueId();
  if (!product?.hasPrice) {
    return (
      <StyledSupplierOffer className="stock supplierOffer">
        { t('product.supplierStock') }:
        <span className="stockAmount">{ product.stockValue } { t('products.products') }</span>
        <Info size={ 18 } id={ `supplier-stock-tooltip-${ uid }}` } />
        <ReactTooltip
          className="stockTooltip"
          anchorId={ `supplier-stock-tooltip-${ uid }}` }
          variant="light"
          offset={ 10 }
          content={ t('products.supplierDeliveryEstimateMessage', { deliveryEstimate: formatWeeksAndDays(product?.supplierOfferDeliveryTimeDays || '') }) }
        />
      </StyledSupplierOffer>
    );
  }

  if (
    (
      product.stockValue >= product.minQuantity
      && product.reservedStockValue >= product.minQuantity
    ) || (
      product.supplierOffersTotalStock > 0
      && product.reservedStockValue <= 3
    )
  ) {
    return (
      <StyledTooltip>
        <p className="stock inStockAndReserved">
          { (product.stockValue === 0 && product.reservedStockValue > 0) ? (
            <>
              { t('products.stockReserved') }:
              <span className="stockAmountReserved">{ product.reservedStockValue } { t('products.products') }</span>
              <span id={ `stock-tooltip-${ uid }}` } className="reservedStock">+{ product.supplierOffersTotalStock }</span>
            </>
          ) : (
            <>
              { t('products.stock') }:
              <span className={ `stockAmount ${ product.stockValue === 0 ? 'noStock' : '' }` }>{ product.stockValue } { t('products.products') }</span>
              <span id={ `stock-tooltip-${ uid }}` } className="reservedStock">+{ product.reservedStockValue + product.supplierOffersTotalStock }</span>
            </>
          )}
          <ReactTooltip
            className="stockTooltip"
            anchorId={ `stock-tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <>
                <div className="stockInfo">
                  <div className="stockInfoLeft">
                    { (product.stockValue > 0) && <span>{ t('products.inStock') }:</span> }
                    { (product.reservedStockValue > 0) && <span>{ t('products.reserved') }:</span> }
                    { (product.supplierOffersTotalStock > 0) && <span>{ t('products.supplierStock') }:</span> }
                  </div>
                  <div className="stockInfoRight">
                    { (product.stockValue > 0) && <span className="stockAmount">{ product.stockValue } { t('products.products') }</span> }
                    { (product.reservedStockValue > 0) && <span>{ product.reservedStockValue } { t('products.products') }</span> }
                    { (product.supplierOffersTotalStock > 0) && <span className="supplierAmount">{ product.supplierOffersTotalStock } { t('products.products') }</span> }
                  </div>
                </div>
                { info?.accountManagerPhone && <p>{ t('products.rsvpDetailsAt') }: <span>{ info?.accountManagerPhone }</span></p> }
              </>
            ) }
          />
        </p>
      </StyledTooltip>
    );
  }

  if (product.stockValue >= product.minQuantity) {
    return (
      <p className="stock inStock">
        { t('products.stock') }:
        <span> { product.stockValue } { t('products.products') }</span>
      </p>
    );
  }

  if (product.reservedStockValue >= product.minQuantity) {
    return (
      <div className="stock">
        <p className="stock reserved">
          { t('products.stockReserved') } <span>{ product.reservedStockValue } { t('products.products') }</span>
        </p>
        <p className="rsvpDetails">
          <span>({ info?.accountManagerPhone ? <>{ t('products.rsvpDetailsAt') } <span className="danger">{ info?.accountManagerPhone }</span></> : t('products.rsvpDetails') }</span>)
        </p>
      </div>
    );
  }

  if (!(product.stockValue > 0 && product.reservedStockValue > 0) && product.inCsp) {
    return (
      <p className="stock inCsp">
        { t('products.stock') }:
        <span>{ t('products.inCsp') }</span>
      </p>
    );
  }

  if (product.supplierStockValue > 0 || product.isOnDemandOnly) {
    if (product.supplierStockDeliveryDate) {
      return (
        <p className="stock onDemand">
          <span>
            { t('products.outOfStock') } ({ product.supplierStockValue } { t('products.units') } { t('products.availableStartingFrom') } { formatDate(product.supplierStockDeliveryDate, { timeStyle: undefined }) })
          </span>
        </p>
      );
    }
    if (product.deliveryTime) {
      return (
        <p className="stock onDemand">
          <span>{ t('products.onDemand') } ({ t('products.deliveryTime', { days: product.deliveryTime }) })</span>
        </p>
      );
    }
    return (
      <p className="stock onDemand">
        <span>{ t('products.outOfStock') } ({ t('products.onDemand') })</span>
      </p>
    );
  }
  return (
    <p className="stock outOfStock">
      { t('products.stock') }:
      <span>{ product?.isEol ? t('products.outOfStock') : '0' }</span>
    </p>
  );
};

export const renderTableStock = ({ product, t, info }) => {
  const uid = uniqueId();
  if (!product?.hasPrice) {
    return (
      <StyledSupplierOffer className="stock supplierOffer table">
        <span className="stockAmount">{ product.stockValue }</span>
        <Info size={ 18 } id={ `supplier-stock-tooltip-${ uid }}` } />
        <ReactTooltip
          className="stockTooltip"
          anchorId={ `supplier-stock-tooltip-${ uid }}` }
          variant="light"
          offset={ 10 }
          content={ t('products.supplierDeliveryEstimateMessage', { deliveryEstimate: formatWeeksAndDays(product?.supplierOfferDeliveryTimeDays || '') }) }
        />
      </StyledSupplierOffer>
    );
  }

  if (
    (
      product.stockValue >= product.minQuantity
      && product.reservedStockValue >= product.minQuantity
    )
    || product.supplierOffersTotalStock > 0
  ) {
    return (
      <StyledTooltip>
        <p className="stock inStockAndReserved">
          { !(product.stockValue === 0 && product.reservedStockValue > 0) && (
            <>
              <span className={ `stockAmount ${ product.stockValue === 0 ? 'noStock' : '' }` }>{ product.stockValue }</span>
              <span id={ `stock-tooltip-${ uid }}` } className="reservedStock">+{ product.reservedStockValue + product.supplierOffersTotalStock }</span>
            </>
          )}
          <ReactTooltip
            className="stockTooltip"
            anchorId={ `stock-tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <>
                <div className="stockInfo">
                  <div className="stockInfoLeft">
                    { (product.stockValue > 0) && <span>{ t('products.inStock') }:</span> }
                    { (product.reservedStockValue > 0) && <span>{ t('products.reserved') }:</span> }
                    { (product.supplierOffersTotalStock > 0) && <span>{ t('products.supplierStock') }:</span> }
                  </div>
                  <div className="stockInfoRight">
                    { (product.stockValue > 0) && <span className="stockAmount">{ product.stockValue } { t('products.products') }</span> }
                    { (product.reservedStockValue > 0) && <span>{ product.reservedStockValue } { t('products.products') }</span> }
                    { (product.supplierOffersTotalStock > 0) && <span className="supplierAmount">{ product.supplierOffersTotalStock } { t('products.products') }</span> }
                  </div>
                </div>
                { info?.accountManagerPhone && <p>{ t('products.rsvpDetailsAt') }: <span>{ info?.accountManagerPhone }</span></p> }
              </>
            ) }
          />
        </p>
      </StyledTooltip>
    );
  }

  if (product.stockValue >= product.minQuantity) {
    return (
      <p className="stock inStock">
        <span> { product.stockValue }</span>
      </p>
    );
  }

  return <></>;
};

export const renderSearchStock = ({ product, t, info }) => {
  const uid = uniqueId();
  if (!product?.hasPrice) {
    return (
      <StyledSupplierOffer className="stock supplierOffer">
        { t('product.supplierStock') }:
        <span className="stockAmount">{ product.stockValue } { t('products.products') }</span>
        <Info size={ 18 } id={ `supplier-stock-tooltip-${ uid }}` } />
        <ReactTooltip
          className="stockTooltip"
          anchorId={ `supplier-stock-tooltip-${ uid }}` }
          variant="light"
          offset={ 10 }
          content={ t('products.supplierDeliveryEstimateMessage', { deliveryEstimate: formatWeeksAndDays(product?.supplierOfferDeliveryTimeDays || '') }) }
        />
      </StyledSupplierOffer>
    );
  }

  if (
    (
      product.stockValue >= product.minQuantity
      && product.reservedStockValue >= product.minQuantity
    ) || (
      product.supplierOffersTotalStock > 0
      && product.reservedStockValue <= 3
    )
  ) {
    return (
      <StyledTooltip>
        <div className="stock inStockAndReserved">
          { (product.stockValue === 0 && product.reservedStockValue > 0) ? (
            <>
              { t('products.stockReserved') }:
              <span className="stockAmountReserved">{ product.reservedStockValue } { t('products.products') }</span>
              <span id={ `stock-tooltip-${ uid }}` } className="reservedStock">+{ product.supplierOffersTotalStock }</span>
            </>
          ) : (
            <>
              { t('products.stock') }:
              <span className={ `stockAmount ${ product.stockValue === 0 ? 'noStock' : '' }` }>{ product.stockValue } { t('products.products') }</span>
              <span id={ `stock-tooltip-${ uid }}` } className="reservedStock">+{ product.reservedStockValue + product.supplierOffersTotalStock }</span>
            </>
          )}
          <ReactTooltip
            className="stockTooltip"
            anchorId={ `stock-tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <>
                <div className="stockInfo">
                  <div className="stockInfoLeft">
                    { (product.stockValue > 0) && <span>{ t('products.inStock') }:</span> }
                    { (product.reservedStockValue > 0) && <span>{ t('products.reserved') }:</span> }
                    { (product.supplierOffersTotalStock > 0) && <span>{ t('products.supplierStock') }:</span> }
                  </div>
                  <div className="stockInfoRight">
                    { (product.stockValue > 0) && <span className="stockAmount">{ product.stockValue } { t('products.products') }</span> }
                    { (product.reservedStockValue > 0) && <span>{ product.reservedStockValue } { t('products.products') }</span> }
                    { (product.supplierOffersTotalStock > 0) && <span className="supplierAmount">{ product.supplierOffersTotalStock } { t('products.products') }</span> }
                  </div>
                </div>
                { info?.accountManagerPhone && <p>{ t('products.rsvpDetailsAt') }: <span>{ info?.accountManagerPhone }</span></p> }
              </>
            ) }
          />
        </div>
      </StyledTooltip>
    );
  }

  if (product.stockValue >= product.minQuantity) {
    return (
      <p className="stock inStock">
        { t('products.stock') }:
        <span>{ product.stockValue } { t('products.products') }</span>
      </p>
    );
  }

  if (product.reservedStockValue >= product.minQuantity) {
    return (
      <div className="stock">
        <p className="stock reserved">
          { t('products.stockReserved') }: <span>{ product.reservedStockValue } { t('products.products') }</span>
        </p>
      </div>
    );
  }

  if (!(product.stockValue > 0 && product.reservedStockValue > 0) && product.inCsp) {
    return (
      <p className="stock inCsp">
        { t('products.stock') }:
        <span>{ t('products.inCsp') }</span>
      </p>
    );
  }

  if (product.supplierStockValue > 0 || product.isOnDemandOnly) {
    if (product.supplierStockDeliveryDate) {
      return (
        <p className="stock onDemand">
          { t('products.stock') }:
          <span>
            { t('products.outOfStock') } ({ product.supplierStockValue } { t('products.units') } { t('products.availableStartingFrom') } { formatDate(product.supplierStockDeliveryDate, { timeStyle: undefined }) })
          </span>
        </p>
      );
    }

    return (
      <p className="stock onDemand">
        <span>{ t('products.outOfStock') } ({ t('products.onDemand') })</span>
      </p>
    );
  }

  return (
    <p className="stock outOfStock">
      { t('products.stock') }:
      <span>{ product?.isEol ? t('products.outOfStock') : '0' }</span>
    </p>
  );
};
